import React, { useState, useEffect } from 'react';
import './Pages.css';

const API_BASE = 'https://main.victriedemos.com:3017';

const UnderwritingCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', logoFile: null, logoPreview: '' });
  const [submitting, setSubmitting] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editName, setEditName] = useState('');
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/underwriting-companies`);
      const data = await response.json();

      if (data.success) {
        setCompanies(data.companies);
      } else {
        setError('Failed to load underwriting companies');
      }
    } catch (err) {
      console.error('Error fetching underwriting companies:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (milliseconds) => {
    if (!milliseconds) return 'N/A';
    const date = new Date(parseInt(milliseconds));
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const logoUrl = (logo) => (logo ? `${API_BASE}${logo}` : '');

  const resetCreateForm = () => {
    setNewCompany({ name: '', logoFile: null, logoPreview: '' });
  };

  const handleCreateLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNewCompany({ ...newCompany, logoFile: file, logoPreview: URL.createObjectURL(file) });
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();

    if (!newCompany.name.trim() || !newCompany.logoFile) {
      alert('Name and logo are required');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', newCompany.name.trim());
      formData.append('logo', newCompany.logoFile);
      formData.append('createdBy', localStorage.getItem('userEmail') || '');

      const response = await fetch(`${API_BASE}/api/underwriting-companies`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        alert('Underwriting company created successfully');
        setShowCreateModal(false);
        resetCreateForm();
        fetchCompanies();
      } else {
        alert(data.message || 'Failed to create underwriting company');
      }
    } catch (err) {
      console.error('Error creating underwriting company:', err);
      alert('Unable to create underwriting company');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setEditName(company.Name);
    setEditLogoFile(null);
    setEditLogoPreview('');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingCompany(null);
    setEditName('');
    setEditLogoFile(null);
    setEditLogoPreview('');
  };

  const handleEditLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditLogoFile(file);
    setEditLogoPreview(URL.createObjectURL(file));
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();

    if (!editName.trim()) {
      alert('Name is required');
      return;
    }

    setEditSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', editName.trim());
      if (editLogoFile) {
        formData.append('logo', editLogoFile);
      }

      const response = await fetch(`${API_BASE}/api/underwriting-companies/${editingCompany.Id}`, {
        method: 'PUT',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        alert('Underwriting company updated successfully');
        closeEditModal();
        fetchCompanies();
      } else {
        alert(data.message || 'Failed to update underwriting company');
      }
    } catch (err) {
      console.error('Error updating underwriting company:', err);
      alert('Unable to update underwriting company');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`Are you sure you want to delete "${company.Name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/underwriting-companies/${company.Id}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        alert('Underwriting company deleted successfully');
        fetchCompanies();
      } else {
        alert(data.message || 'Failed to delete underwriting company');
      }
    } catch (err) {
      console.error('Error deleting underwriting company:', err);
      alert('Unable to delete underwriting company');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Underwriting Companies</h1>
        </div>
        <div className="loading">Loading underwriting companies...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Underwriting Companies</h1>
          <p className="page-description">Manage the underwriting companies used for quote comparisons</p>
        </div>
        <button className="primary-btn" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-plus-circle"></i> Add Company
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="products-grid">
        {companies.length === 0 ? (
          <div className="no-data">No underwriting companies found</div>
        ) : (
          companies.map((company) => (
            <div key={company.Id} className="product-card underwriting-company-card">
              <div className="product-card-content">
                <div className="underwriting-company-logo-wrap">
                  <img src={logoUrl(company.Logo)} alt={company.Name} className="underwriting-company-logo" />
                </div>
                <h3 className="product-name">{company.Name}</h3>
                <div className="underwriting-company-code">{company.Code}</div>
                <p className="underwriting-company-date">Added {formatDate(company.DateCreated)}</p>
                <div className="product-actions">
                  <button
                    className="btn-view-product"
                    onClick={() => openEditModal(company)}
                    title="Edit Company"
                  >
                    <i className="bi bi-pencil"></i> Edit
                  </button>
                  <button
                    className="btn-delete-product"
                    onClick={() => handleDeleteCompany(company)}
                    title="Delete Company"
                  >
                    <i className="bi bi-trash"></i> Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Underwriting Company</h2>
              <button className="close-button" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <form onSubmit={handleCreateCompany}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Company Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                    placeholder="e.g., The Monarch"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Logo <span className="required">*</span></label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCreateLogoChange}
                    required
                  />
                  {newCompany.logoPreview && (
                    <img src={newCompany.logoPreview} alt="Logo preview" className="logo-preview" />
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetCreateForm(); }}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditModal && editingCompany && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Underwriting Company</h2>
              <button className="close-button" onClick={closeEditModal}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <form onSubmit={handleUpdateCompany}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Company Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Logo</label>
                  <img
                    src={editLogoPreview || logoUrl(editingCompany.Logo)}
                    alt={editingCompany.Name}
                    className="logo-preview"
                  />
                  <input type="file" accept="image/*" onChange={handleEditLogoChange} />
                  <small className="form-hint">Leave empty to keep the current logo</small>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={closeEditModal} className="btn-secondary" disabled={editSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="primary-btn" disabled={editSubmitting}>
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnderwritingCompanies;
